import type { components } from '@slims-pro/api-client';
import type { ExtFeatureKey, FeatureKey } from '@slims-pro/client-config';
import type { ProSectionId } from '@slims-pro/ui/organisms';
import { type ComponentType, lazy } from 'react';

export type ModuleId = components['schemas']['ModuleId'];

// Compile-time proof that the API's module ids and the UI's section ids are the same set
// (a type, not a const, so no-unused-vars stays quiet).
type Equals<A, B> = [A] extends [B] ? ([B] extends [A] ? true : false) : false;
type Assert<T extends true> = T;
export type AssertModuleIdsMatchSections = Assert<Equals<ModuleId, ProSectionId>>;

export interface ModuleRoute {
	path: string; // equals the id
	title: string; // browser tab title
	/** Lazy: the page's chunk loads only once the module is enabled and permitted (ADR-008). */
	Page: ComponentType;
	/** The route exists only while this feature key is enabled. */
	feature?: FeatureKey | ExtFeatureKey;
}

export const MODULE_ROUTES: Record<ModuleId, ModuleRoute> = {
	fleet: {
		path: 'fleet',
		title: 'Fleet Multi-Sito',
		Page: lazy(() => import('@/pages/fleet-page').then((m) => ({ default: m.ProFleetPage }))),
	},
	single: {
		path: 'single',
		title: 'Impianto Singolo',
		Page: lazy(() => import('@/pages/single-page').then((m) => ({ default: m.ProSinglePage }))),
	},
	field: {
		path: 'field',
		title: 'Campo Utility-Scale',
		Page: lazy(() => import('@/pages/field-page').then((m) => ({ default: m.ProFieldPage }))),
	},
	assets: {
		path: 'assets',
		title: 'Asset & Impianti',
		Page: lazy(() => import('@/pages/assets-page').then((m) => ({ default: m.ProAssetsPage }))),
	},
	topology: {
		path: 'topology',
		title: 'Cabina Primaria',
		Page: lazy(() => import('@/pages/topology-page').then((m) => ({ default: m.ProTopologyPage }))),
	},
	inventory: {
		path: 'inventory',
		title: 'Inventario Ricambi',
		Page: lazy(() =>
			import('@/pages/inventory-page').then((m) => ({ default: m.ProInventoryPage })),
		),
	},
	monitor: {
		path: 'monitor',
		title: 'Monitoraggio Energia',
		Page: lazy(() => import('@/pages/monitor-page').then((m) => ({ default: m.ProMonitorPage }))),
	},
	forecast: {
		path: 'forecast',
		title: 'Forecast & Meteo',
		Page: lazy(() => import('@/pages/forecast-page').then((m) => ({ default: m.ProForecastPage }))),
	},
	design: {
		path: 'design',
		title: 'Dimensionamento FV',
		Page: lazy(() => import('@/pages/design-page').then((m) => ({ default: m.ProDesignPage }))),
	},
	docs: {
		path: 'docs',
		title: 'Pratiche GSE/STMG',
		Page: lazy(() => import('@/pages/docs-page').then((m) => ({ default: m.ProDocsPage }))),
	},
	anomaly: {
		path: 'anomaly',
		title: 'Rilevamento Anomalie AI',
		Page: lazy(() => import('@/pages/anomaly-page').then((m) => ({ default: m.ProAnomalyPage }))),
	},
	solutions: {
		path: 'solutions',
		title: 'Soluzioni Operative',
		Page: lazy(() =>
			import('@/pages/solutions-page').then((m) => ({ default: m.ProSolutionsPage })),
		),
	},
	predictive: {
		path: 'predictive',
		title: 'Manutenzione Predittiva',
		Page: lazy(() =>
			import('@/pages/predictive-page').then((m) => ({ default: m.ProPredictivePage })),
		),
	},
	oman: {
		path: 'oman',
		title: 'Economica O&M',
		Page: lazy(() => import('@/pages/oman-page').then((m) => ({ default: m.ProOmanPage }))),
	},
	rendiconto: {
		path: 'rendiconto',
		title: 'Rendiconto Socio/Cliente',
		Page: lazy(() =>
			import('@/pages/rendiconto-page').then((m) => ({ default: m.ProRendicontoPage })),
		),
	},
	warranty: {
		path: 'warranty',
		title: 'Garanzie & Compliance',
		Page: lazy(() => import('@/pages/warranty-page').then((m) => ({ default: m.ProWarrantyPage }))),
	},
	roles: {
		path: 'roles',
		title: 'Accessi & Ruoli',
		Page: lazy(() => import('@/pages/roles-page').then((m) => ({ default: m.ProRolesPage }))),
	},
	reports: {
		path: 'reports',
		title: 'Report Tecnici',
		Page: lazy(() => import('@/pages/reports-page').then((m) => ({ default: m.ProReportsPage }))),
	},
	config: {
		path: 'config',
		title: 'Configurazione',
		Page: lazy(() => import('@/pages/config-page').then((m) => ({ default: m.ProConfigPage }))),
	},
};
